import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Field, Form, useFormikContext } from 'formik'
import {
  BaseTextField,
  MuiSwitchFormik,
  MuiTextFieldFormik,
} from '@user/components/Formik'
import MenuItem from '@mui/material/MenuItem'
import Autocomplete from '@mui/material/Autocomplete'
import {
  SERVICE_TYPE,
  serviceProductDisplayName,
} from '@user/store/serviceProducts'
import TextField from '@mui/material/TextField'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { CurrencyFieldWithNegativePrice } from '@user/components/Formik/CurrencyFieldWithNegativePrice'
import { CurrencyPercentageField } from '@user/components/Services/AddServiceForm/CurrencyPercentageField'
import { formControlLabelClasses } from '@mui/material/FormControlLabel'
import { AddServiceFormFooter } from '@user/components/Services/AddServiceForm/AddServiceFormFooter'
import {
  useAutocomplete,
  useCalculateTotals,
  useUpdateFormFields,
} from '@user/components/Services/AddServiceForm/hooks'
import { useLocalizationEnabled } from '@user/store/tenantFeatures/hooks/useLocalizationEnabled'
import { ILeadOppProductFormModel } from '@user/components/Services/AddServiceForm/typings'
import { useTheme } from '@mui/material/styles'
import { getStyles } from '@user/components/Services/AddServiceForm/useStyles'
import {
  currencyNumber,
  currencyString,
  getServiceLocationTitle,
} from '@sales-center/common'
import { InfoTooltip } from '@sales-center/components'
import { Props as FormProps } from '@user/components/Services/AddServiceForm/AddServiceForm'
import useMediaQuery from '@mui/material/useMediaQuery'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'

interface Props
  extends Omit<FormProps, 'initialValues'>,
    Pick<
      ReturnType<typeof useUpdateFormFields>,
      'onChangeExternalId' | 'onChangeFrequencyId' | 'servicesLoading'
    > {
  editMode: boolean
}

export const AddServiceFormFieldsContent = (props: Props) => {
  const {
    isMobile,
    localMode,
    serviceFrequencies,
    serviceLocations,
    onChangeExternalId,
    onChangeFrequencyId,
    servicesLoading,
  } = props

  const isXS = useMediaQuery('(max-width:480px)')
  const { errors, values, touched, setFieldValue } =
    useFormikContext<ILeadOppProductFormModel>()

  const { autoCompleteOptions, autoCompleteValue } = useAutocomplete(props)
  const theme = useTheme()
  const styles = getStyles(theme)
  const {
    taxRate,
    chargeTax,
    quantity,
    annualOccurrences,
    serviceType,
    canadianTaxRate,
    gstHstLabel,
    gstHstRate,
    pstQstLabel,
    pstQstRate,
  } = values

  const isCanadianTax = useLocalizationEnabled() && canadianTaxRate
  const {
    initialTax,
    initialTotalLabel,
    initialTotalNoTax,
    recurringTax,
    recurringTotalLabel,
    recurringTotalNoTax,

    initialGSTTax,
    initialPSTTax,
    recurringGSTTax,
    recurringPSTTax,
  } = useCalculateTotals(isCanadianTax)

  // WWM-18150 - Formik's field value can transiently point at a location that's no longer
  // (or not yet) present in serviceLocations - e.g. a stale drawer still mid-close-transition
  // while the store moves on to a different entity. Rather than letting the Select render an
  // out-of-range value (MUI console warning, confusing-looking blank/invalid selection),
  // constrain what's actually displayed to a value that's genuinely one of the current options.
  const validServiceLocationIds = useMemo(
    () =>
      new Set(
        serviceLocations.map((l) =>
          (localMode ? Number(l.externalLinkId) : l.id).toString(),
        ),
      ),
    [serviceLocations, localMode],
  )
  const safeServiceLocationId = validServiceLocationIds.has(
    values.serviceLocationId,
  )
    ? values.serviceLocationId
    : ''

  const onUpdate = useCallback(
    (n: string, v: string) => {
      if (Number(currencyNumber(v)) < 0) {
        if (n === 'recurringPrice') {
          setFieldValue('recurringDiscountAmount', '')
        } else if (n === 'initialPrice') {
          setFieldValue('initialDiscountAmount', '')
        }
      }
    },
    [setFieldValue],
  )

  return (
    <Form>
      <Grid container>
        <Box mt={isMobile ? 1 : 2} mb={2}>
          <Typography variant="h7">Select Service Location</Typography>
        </Box>

        <Grid item xs={12}>
          <Field
            name="serviceLocationId"
            label="Service Location"
            component={BaseTextField}
            value={safeServiceLocationId}
            // onChange={onChangeLocation}
            select
            required
            fullWidth
            size="small"
          >
            {serviceLocations.map((l) => {
              const value = localMode ? Number(l.externalLinkId) : l.id

              return (
                <MenuItem key={value} value={value.toString()}>
                  {getServiceLocationTitle(l)}
                </MenuItem>
              )
            })}
          </Field>
        </Grid>

        <Divider sx={{ mt: 3, mb: 3, width: '100%' }} />

        <Box mb={1.5}>
          <Typography variant="h7">Service Details</Typography>
        </Box>

        <Grid item xs={12} style={{ paddingBottom: theme.spacing(1) }}>
          <Autocomplete
            id="services"
            fullWidth
            size="small"
            value={autoCompleteValue}
            options={autoCompleteOptions}
            getOptionLabel={serviceProductDisplayName}
            groupBy={(x) =>
              x.serviceType == SERVICE_TYPE.SERVICE ? 'Service' : 'Product'
            }
            onChange={onChangeExternalId}
            loading={servicesLoading}
            loadingText="Loading Services"
            renderInput={(params) => (
              <TextField
                {...params}
                name="externalId"
                label="Service or Product"
                required
                variant="outlined"
                error={touched.externalId && !!errors.externalId}
                helperText={touched.externalId ? errors.externalId : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {servicesLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        {/* qty */}
        <Grid item xs>
          <Field
            name="quantity"
            label="Quantity"
            component={MuiTextFieldFormik}
            type="number"
            required
            fullWidth
            size="small"
          />
        </Grid>
        {/* freq */}
        {serviceType == SERVICE_TYPE.SERVICE && (
          <Grid item xs={isXS ? 12 : 6} sx={isXS ? null : styles.frequency}>
            <Field
              name="frequencyId"
              label="Frequency"
              component={MuiTextFieldFormik}
              select
              required
              fullWidth
              size="small"
              onChange={(e) =>
                onChangeFrequencyId(e, values.frequencyId, values.externalId)
              }
            >
              {serviceFrequencies.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {`${f.annualOccurrences} - ${f.description}`}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        )}

        {/* upsell */}
        <Grid item xs={12} sx={{ p: 0, mt: 1.5 }}>
          <Field
            name="upSell"
            component={MuiSwitchFormik}
            label="Mark as Upsell"
          />
        </Grid>

        <Divider sx={{ mt: 1.5, mb: 3, width: '100%' }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h7">Pricing</Typography>
        </Box>

        {/* initial */}
        <Stack sx={{ backgroundColor: '#F9FAFB', p: 2, width: '100%' }}>
          <Typography variant="subtitle4" mb="12px">
            Initial Visit
          </Typography>
          <Grid container item spacing={1}>
            <Grid container item xs={isXS ? 12 : 6} direction="column">
              <Grid item container alignItems="center">
                <Typography variant="body1">Price</Typography>

                <InfoTooltip
                  infoText="Price for initial service"
                  tooltipPlacement="top"
                  sx={styles.info}
                />
              </Grid>
              <Grid item>
                <CurrencyFieldWithNegativePrice
                  name="initialPrice"
                  required
                  margin="dense"
                  size="medium"
                  onUpdate={onUpdate}
                  fullWidth
                  placeholder="$0.00"
                  InputProps={{
                    sx: {
                      height: '37px',
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container item xs={isXS ? 8 : 3} direction="column">
              <Grid item container alignItems="center">
                <Typography variant="body1">Discount</Typography>
                <InfoTooltip
                  infoText="Enter a dollar amount or percentage"
                  tooltipPlacement="top"
                  sx={styles.info}
                />
              </Grid>
              <Grid item>
                <CurrencyPercentageField
                  name="initialDiscountAmount"
                  size="medium"
                  margin="dense"
                  fullWidth
                  disabled={Number(currencyNumber(values.initialPrice)) < 0}
                  placeholder="$0.00"
                  InputProps={{
                    sx: {
                      height: '37px',
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container item xs direction="column">
              <Grid item>
                <Typography variant="body1">Total</Typography>
              </Grid>
              <Grid item sx={styles.total}>
                {initialTotalLabel}
              </Grid>
            </Grid>
          </Grid>
          {chargeTax && (
            <span style={styles.tax}>
              {isCanadianTax
                ? `${gstHstLabel} ${currencyString(initialGSTTax)} (${
                    gstHstRate * 100
                  }%)\n${pstQstLabel} ${currencyString(initialPSTTax)} (${
                    pstQstRate * 100
                  }%)`
                : `Tax ${currencyString(initialTax)} (${taxRate * 100}%)`}
            </span>
          )}
        </Stack>

        {/* recurring */}
        {annualOccurrences > 0 && (
          <>
            <Stack
              sx={{ backgroundColor: '#F9FAFB', p: 2, width: '100%', mt: 3 }}
            >
              <Typography variant="subtitle4" mb="12px">
                Recurring
              </Typography>
              <Grid container item spacing={1}>
                <Grid container item xs={isXS ? 12 : 6} direction="column">
                  <Grid item container alignItems="center">
                    <Typography variant="body1">Price</Typography>

                    <InfoTooltip
                      infoText="Price for recurring service"
                      tooltipPlacement="top"
                      sx={styles.info}
                    />
                  </Grid>
                  <Grid item>
                    <CurrencyFieldWithNegativePrice
                      name="recurringPrice"
                      required
                      margin="dense"
                      size="medium"
                      onUpdate={onUpdate}
                      fullWidth
                      placeholder="$0.00"
                      InputProps={{
                        sx: {
                          height: '37px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container item xs={isXS ? 8 : 3} direction="column">
                  <Grid item container alignItems="center">
                    <Typography variant="body1">Discount</Typography>
                    <InfoTooltip
                      infoText="Enter a dollar amount or percentage"
                      tooltipPlacement="top"
                      sx={styles.info}
                    />
                  </Grid>
                  <Grid item>
                    <CurrencyPercentageField
                      name="recurringDiscountAmount"
                      size="medium"
                      margin="dense"
                      fullWidth
                      disabled={
                        Number(currencyNumber(values.recurringPrice)) < 0
                      }
                      placeholder="$0.00"
                      InputProps={{
                        sx: {
                          height: '37px',
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container item xs direction="column">
                  <Grid item>
                    <Typography variant="body1">Total</Typography>
                  </Grid>
                  <Grid item sx={styles.total}>
                    {recurringTotalLabel}
                  </Grid>
                </Grid>
              </Grid>
              {chargeTax && (
                <span style={styles.tax}>
                  {isCanadianTax
                    ? `${gstHstLabel} ${currencyString(
                        recurringGSTTax * quantity,
                      )} (${gstHstRate * 100}%)\n${pstQstLabel} ${currencyString(
                        recurringPSTTax * quantity,
                      )} (${pstQstRate * 100}%)`
                    : `Tax ${currencyString(recurringTax * quantity)} (${
                        taxRate * 100
                      }%)`}
                </span>
              )}
            </Stack>
          </>
        )}
        <Grid
          item
          xs={12}
          mt={3}
          ml={1}
          style={{ paddingBottom: theme.spacing(0) }}
        >
          <Field
            name="chargeTax"
            component={MuiSwitchFormik}
            label={`Include ${!isCanadianTax ? 'Sales' : ''} Tax`}
            labelProps={{
              sx: {
                [`.${formControlLabelClasses.root}`]: styles.switchRoot,
                [`.${formControlLabelClasses.label}`]: styles.switchLabel,
              },
            }}
          />
        </Grid>

        {annualOccurrences > 0 && (
          <Grid item xs={12} ml={1}>
            <Field
              name="subtractOne"
              component={MuiSwitchFormik}
              label="Subtract one recurring service from first year price"
              labelProps={{
                sx: {
                  [`.${formControlLabelClasses.root}`]: styles.switchRoot,
                  [`.${formControlLabelClasses.label}`]: styles.switchLabel,
                },
              }}
            />
          </Grid>
        )}

        <Divider sx={{ mt: 2, mb: 0, width: '100%' }} />

        <AddServiceFormFooter
          initialTax={initialTax}
          initialTotal={initialTotalNoTax}
          recurringTax={recurringTax}
          recurringTotal={recurringTotalNoTax}
          values={values}
          isCanadianTax={isCanadianTax}
          initialGSTTax={initialGSTTax}
          initialPSTTax={initialPSTTax}
          recurringGSTTax={recurringGSTTax}
          recurringPSTTax={recurringPSTTax}
        />
      </Grid>
    </Form>
  )
}
